import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CarWashPackage } from "../../types/carWash.types";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import {
  fetchSlots,
  slotLocking,
  slotUnlocking,
} from "../../redux/slices/slotSlice";
import { SlotTime } from "../../types/slots.types";
import { City } from "../../types/city.types";
import eventBus from "../../utils/eventBus";
import { LOCATION_CHANGED } from "../../constants/events";
import { setLoginModalState } from "../../redux/slices/popupModalSlices";
import { getMyCars } from "../../redux/slices/carSlice";

interface CarWashSelectedProps {
  handleClick: (pkg: CarWashPackage) => void;
  selectedPackage: CarWashPackage;
  handleBooking: (option: {
    date: string;
    time: string;
    price: number;
    carId?: string;
  }) => void;
}

export const CarWashSelected = ({
  handleClick,
  selectedPackage,
  handleBooking,
}: CarWashSelectedProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [scheduledDate, setScheduledDate] = useState<Date | null>(null);
  const [selectedCar, setSelectedCar] = useState<any | null>(null);
  const [showCarPopup, setShowCarPopup] = useState(false);

  const auth = useSelector((state: RootState) => state.auth);
  const { selectedCity } = useSelector((state: RootState) => state.city);
  const {
    date: slotDate,
    slotTimes,
    city,
    country,
    lockedSlot,
  } = useSelector((state: RootState) => state.slots);
  const myCars = useSelector((state: RootState) => state?.myCars);

  const [bookingDisabledMsg, setBookingDisabledMsg] = useState("");
  const [updatedCarWashPrice, setUpdatedCarWashPrice] = useState("");
  const [selectedOption, setSelectedOption] = useState<{
    date: string;
    time: string;
    price: number;
  }>({
    date: slotDate || "",
    time: "",
    price: updatedCarWashPrice ? Number(updatedCarWashPrice) : 0,
  });

  useEffect(() => {
    if (auth.user && auth.user.cars?.length > 0 && !myCars?.cars?.length) {
      dispatch(getMyCars(auth.user.cars) as any);
    }
  }, [auth.user]);

  useEffect(() => {
    const priceDifference =
      Number(selectedPackage.priceTo) - Number(selectedPackage.priceFrom);
    const perTypePrice = priceDifference / 3;
    if (selectedCar?.carType === "hatchback") {
      setUpdatedCarWashPrice(
        (Number(selectedPackage.priceFrom) + perTypePrice * 1).toString()
      );
    }
    if (selectedCar?.carType === "sedan") {
      setUpdatedCarWashPrice(
        (Number(selectedPackage.priceFrom) + perTypePrice * 2).toString()
      );
    }
    if (selectedCar?.carType === "suv") {
      setUpdatedCarWashPrice(
        (Number(selectedPackage.priceFrom) + perTypePrice * 3).toString()
      );
    }
  }, [selectedCar, myCars.cars]);

  useEffect(() => {
    if (!selectedCity?.city) {
      setBookingDisabledMsg(t("selectCityWarning"));
    } else if (selectedCity?.city && !selectedCity?.isAvailable) {
      setBookingDisabledMsg(t("serviceUnavailableCity"));
    } else {
      setBookingDisabledMsg("");
    }
  }, [selectedCity]);

  useEffect(() => {
    const handleLocationChange = ({ city }: { city: City }) => {
      setScheduledDate(null);
      setSelectedOption({ date: "", time: "", price: 0 });
      setSelectedCar(null);
    };

    eventBus.on(LOCATION_CHANGED, handleLocationChange);

    return () => {
      eventBus.off(LOCATION_CHANGED, handleLocationChange);
    };
  }, []);

  const handleDateChange = (date: Date | null) => {
    if (!auth?.token || !auth?.user?.name) {
      dispatch(setLoginModalState(true));
      return;
    }

    if (bookingDisabledMsg) {
      toast.error(bookingDisabledMsg);
      return;
    }

    if (lockedSlot) {
      dispatch(
        slotUnlocking({
          city,
          country,
          date: slotDate,
          slotTime: lockedSlot?.slotTime,
        }) as any
      );
    }

    setScheduledDate(date);
    setSelectedOption({
      date: date ? date.toDateString() : "",
      time: "",
      price: updatedCarWashPrice ? Number(updatedCarWashPrice) : 0,
    });

    dispatch(
      fetchSlots({
        city: selectedCity?.city || "",
        country: selectedCity?.country || "",
        date: format(date as any, "dd MMM yyyy") || "",
      }) as any
    );
  };

  const handleSlotSelect = (slot: SlotTime) => {
    setSelectedOption((prev) => ({
      ...prev,
      time: slot?.slotTime,
    }));

    dispatch(
      slotLocking({
        city,
        country,
        date: format(scheduledDate as any, "dd MMM yyyy") || "",
        slotTime: slot?.slotTime,
        previousSlotTime: lockedSlot?.slotTime || "",
      }) as any
    );
  };

  return (
    <>
      <div className="rounded animate-fade-in-up bg-popupLight dark:bg-popupDark p-6 relative w-[90%] mt-5 mb-10 shadow-lg mx-auto">
        <button
          onClick={() => navigate("/wash")}
          className="absolute top-6 left-6 p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 z-20 bg-primaryBg/40 dark:bg-primaryBgDark/40 
             hover:bg-primaryBg/80 dark:hover:bg-primaryBgDark/80 transition"
          aria-label="Go back"
        >
          <ArrowLeft size={24} />
        </button>

        <div className="flex flex-col md:flex-row gap-8 max-w-5xl mx-auto">
          <img
            src={selectedPackage?.imgSrc}
            alt={selectedPackage?.title}
            className="rounded-lg shadow-sm max-w-sm w-full object-cover"
          />
          <div className="flex flex-col justify-start flex-1">
            <h1 className="text-3xl font-bold mb-4 text-primaryText dark:text-primaryTextDark">
              {selectedPackage.title}
            </h1>

            <p className="text-primaryText dark:text-primaryTextDark mb-6">
              {selectedPackage.description}
            </p>
            {selectedPackage?.services &&
              selectedPackage?.services?.length > 0 && (
                <ul className="list-disc list-inside text-primaryText dark:text-primaryTextDark mb-6">
                  <p className="text-primaryText dark:text-primaryTextDark mb-2">
                    {t("includedServices") || "Included Services"}
                  </p>
                  {selectedPackage.services.map((service, index) => (
                    <li key={index}>{service}</li>
                  ))}
                </ul>
              )}

            {!updatedCarWashPrice ? (
              <div className="text-2xl font-extrabold text-primaryText dark:text-primaryTextDark mb-8">
                ₹{selectedPackage.priceFrom} - ₹{selectedPackage.priceTo}
              </div>
            ) : (
              <div className="text-2xl font-extrabold text-primaryText dark:text-primaryTextDark mb-8">
                ₹{updatedCarWashPrice}
              </div>
            )}

            <div className="flex items-center gap-4 max-w-sm w-full">
              <label className="text-primaryText dark:text-primaryTextDark whitespace-nowrap">
                {t("scheduleWash") || "Schedule Your Wash"}
              </label>
              <DatePicker
                selected={scheduledDate}
                onChange={handleDateChange}
                minDate={new Date(Date.now() + 86400000)}
                customInput={
                  <button className="px-4 py-3 rounded-md bg-primaryBg dark:bg-primaryBgDark text-primaryText dark:text-primaryTextDark">
                    {scheduledDate
                      ? format(scheduledDate, "dd MMM yyyy")
                      : `${t("selectDate")}`}
                  </button>
                }
                popperPlacement="top-start"
              />
            </div>

            {scheduledDate && (
              <div className="mt-6 animate-fade-in-up">
                <p className="mb-2 text-primaryText dark:text-primaryTextDark">
                  {t("selectTime") || "Select a Time Slot"}
                </p>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {slotTimes &&
                    slotTimes.map((slot) => (
                      <button
                        key={slot._id}
                        disabled={
                          slot.booked?.length + slot?.locked?.length >=
                          slot.maxBookings
                        }
                        onClick={() => handleSlotSelect(slot)}
                        className={`${
                          selectedOption.time === slot?.slotTime
                            ? "bg-buttonHoverLight dark:bg-buttonHoverDark"
                            : "bg-primaryBg dark:bg-primaryBgDark"
                        } py-2 px-2 rounded-md text-primaryText dark:text-primaryTextDark shadow-sm transition hover:bg-buttonHoverLight dark:hover:bg-buttonHoverDark disabled:opacity-50 disabled:hover:bg-primaryBg dark:disabled:hover:bg-primaryBgDark`}
                      >
                        {slot.slotTime}
                      </button>
                    ))}
                </div>
              </div>
            )}

            <div className="mt-8 flex gap-3 items-stretch">
              <button
                disabled={!selectedOption.date || !selectedOption.time}
                onClick={() => {
                  if (!selectedCar) {
                    setShowCarPopup(true);
                    return;
                  }

                  handleBooking({
                    ...selectedOption,
                    carId: selectedCar._id,
                    price: updatedCarWashPrice
                      ? Number(updatedCarWashPrice)
                      : selectedOption.price,
                  });
                }}
                className="flex-1 py-3 rounded-md text-primaryText disabled:text-primaryText/60 font-semibold transition bg-secondaryText dark:bg-secondaryTextDark hover:bg-secondaryText/80 dark:hover:bg-secondaryTextDark/80 shadow-sm disabled:bg-secondaryText/60 dark:disabled:bg-secondaryTextDark/60"
              >
                {selectedCar
                  ? `${t("bookNow") || "Book Now"} (${selectedCar.number})`
                  : t("bookNow") || "Book Now"}
              </button>

              {selectedCar && (
                <button
                  onClick={() => setShowCarPopup(true)}
                  className="min-w-[120px] py-3 rounded-md text-primaryText font-semibold transition bg-secondaryText dark:bg-secondaryTextDark hover:bg-secondaryText/80 dark:hover:bg-secondaryTextDark/80 shadow-sm"
                >
                  {t("Change Car")}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {showCarPopup && (
        <div
          className="fixed text-primaryText dark:text-primaryTextDark inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setShowCarPopup(false)}
        >
          <div
            className="bg-popupLight dark:bg-popupDark rounded-lg p-6 w-[90%] max-w-md animate-fade-in-up"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold mb-4 text-primaryText dark:text-primaryTextDark">
              {t("Select Your Car")}
            </h2>

            {myCars?.cars?.length > 0 ? (
              <div className="flex flex-col gap-3">
                {myCars.cars.map((car: any) => (
                  <button
                    key={car._id}
                    onClick={() => {
                      setSelectedCar(car);
                      setShowCarPopup(false);
                    }}
                    className="py-3 px-4 rounded-md bg-primaryBg dark:bg-primaryBgDark text-left"
                  >
                    {car.number} - {car.brand} {car.model}
                  </button>
                ))}
              </div>
            ) : (
              <p>{t("No cars found. Please add a car.")}</p>
            )}
          </div>
        </div>
      )}
    </>
  );
};
